import type { SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import { type FC } from "react";
import { formatEmpty } from "../../../utils";
import { cn } from "../../lib/utils";
import { EditableMetricCard } from "../../patterns/metric-card";
import { YesNo } from "../../patterns/radio-select";
import { useEditableProfitability } from "../hooks/useEditableProfitability";

export type EditableProfitabilityCardProps = {
	testId?: string;
	label: string;
	accountId: SuretyAccountId;
	additionalInfo?: string;
	isProfitable?: boolean;
	isFye?: boolean;
	startInEditMode?: boolean;
	onEditClose?: () => void;
	// When true the card is display-only — no edit affordance is rendered.
	readOnly?: boolean;
};

const profitableDisplay = (profitable: boolean | null) =>
	formatEmpty(profitable === null ? null : profitable ? "Yes" : "No");

export const EditableProfitabilityCard: FC<EditableProfitabilityCardProps> = ({
	testId = "editable-profitability-card",
	label,
	accountId,
	additionalInfo,
	isProfitable,
	isFye,
	startInEditMode,
	onEditClose,
	readOnly = false,
}) => {
	const {
		isEditing,
		displayProfitable,
		selectedProfitable,
		mutationError,
		startEditing,
		cancelEditing,
		selectProfitable,
		submit,
		dismissError,
		canSubmit,
	} = useEditableProfitability({
		accountId,
		isFye,
		isProfitable,
		startInEditMode: startInEditMode && !readOnly,
		onEditClose,
	});

	const showEditing = isEditing && !readOnly;

	return (
		<EditableMetricCard
			testId={testId}
			label={label}
			readOnly={readOnly}
			errorHeading="Update Profitability Error"
			controller={{
				isEditing,
				canSubmit,
				mutationError,
				startEditing,
				cancelEditing,
				submit,
				dismissError,
			}}
		>
			{showEditing ? (
				<div data-testid={`${testId}-radio-group`}>
					<YesNo
						value={selectedProfitable}
						onChange={(value) => {
							if (value !== null) selectProfitable(value);
						}}
						label={`Profitable ${isFye ? "last" : "this"} year?`}
						size="sm"
					/>
				</div>
			) : (
				<EditableMetricCard.Value
					data-testid={`${testId}-display`}
					className={cn(displayProfitable === false && "text-danger")}
				>
					{profitableDisplay(displayProfitable)}
				</EditableMetricCard.Value>
			)}
			{!isEditing && Option.isSome(displayProfitable) && additionalInfo && (
				<EditableMetricCard.Caption>{additionalInfo}</EditableMetricCard.Caption>
			)}
		</EditableMetricCard>
	);
};
